import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Pressable, Dimensions, Platform } from 'react-native';
import { Card, Text, Button, IconButton, Divider, Badge, Portal } from 'react-native-paper';
import { useNotifications, INotification } from '../../providers/NotificationProvider';
import { useNavigation } from '@react-navigation/core';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParams } from '../Home';

interface NotificationPanelProps {
    visible: boolean;
    onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ visible, onClose }) => {
    const { 
        notifications, 
        unreadCount, 
        markAsRead, 
        markAllAsRead, 
        clearNotification, 
        clearAll,
        addNotification 
    } = useNotifications();

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParams>>();
    const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'critical'>('all');

    if (!visible) return null;

    // Filter notifications based on tab
    const filteredNotifications = notifications.filter(n => {
        if (activeTab === 'unread') return !n.read;
        if (activeTab === 'critical') return n.type === 'critical';
        return true;
    });

    const getIconInfo = (type: INotification['type']) => {
        switch (type) {
            case 'critical':
                return { icon: 'alert-circle', color: '#BA1A1A', bg: '#FFDAD6' };
            case 'warning':
                return { icon: 'alert', color: '#7D5800', bg: '#FFDF9E' };
            case 'success':
                return { icon: 'check-circle', color: '#102B27', bg: '#CBEBE4' };
            case 'info':
            default:
                return { icon: 'information', color: '#0061A4', bg: '#D1E4FF' };
        }
    };

    const handleNotificationClick = (item: INotification) => {
        markAsRead(item.id);
        
        // If there's an actionRoute and we can navigate
        if (item.actionRoute) {
            onClose();
            // Basic check to see if we can find the route
            // For now, if it is Environmental Issues, we can search or navigate
            // If the route is a standard stack route, we navigate there. Otherwise we show details
        }
    };

    const seedDemoNotification = () => {
        const demoTypes: Array<Omit<INotification, 'id' | 'timestamp' | 'read'>> = [
            {
                title: 'High PM10 Level',
                description: 'PM10 sensor reading reached 152 µg/m³ at North Gate.',
                type: 'critical',
                category: 'PM10'
            },
            {
                title: 'Spill Kit Check',
                description: 'Monthly inspection of spill response kits is due.',
                type: 'warning',
                category: 'Observations'
            },
            {
                title: 'New Site Permit',
                description: 'Water Discharge Permit renewed successfully by local authorities.',
                type: 'success',
                category: 'Permits'
            },
            {
                title: 'System Update',
                description: 'Greenport dashboard analytics modules updated to v2.4.',
                type: 'info',
                category: 'System'
            }
        ];

        const randomNotif = demoTypes[Math.floor(Math.random() * demoTypes.length)];
        addNotification(randomNotif);
    };

    return (
        <Portal>
            <Card style={styles.cardContainer}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.headerTitleRow}>
                        <Text variant="titleMedium" style={styles.headerTitle}>
                            Alerts & Issues
                        </Text>
                        {unreadCount > 0 && (
                            <Badge style={styles.headerBadge} size={20}>
                                {unreadCount}
                            </Badge>
                        )}
                    </View>
                    <View style={styles.headerActionRow}>
                        {unreadCount > 0 && (
                            <Button 
                                mode="text" 
                                compact 
                                onPress={markAllAsRead} 
                                textColor="#4BB75F"
                                labelStyle={styles.actionBtnText}
                            >
                                Mark all read
                            </Button>
                        )}
                        <IconButton 
                            icon="close" 
                            size={18} 
                            style={styles.closeIcon}
                            onPress={onClose} 
                        />
                    </View>
                </View>

                {/* Quick Filter Tabs */}
                <View style={styles.tabContainer}>
                    <Pressable 
                        onPress={() => setActiveTab('all')} 
                        style={[styles.tab, activeTab === 'all' && styles.activeTab]}
                    >
                        <Text style={[styles.tabText, activeTab === 'all' && styles.activeTabText]}>
                            All ({notifications.length})
                        </Text>
                    </Pressable>
                    <Pressable 
                        onPress={() => setActiveTab('unread')} 
                        style={[styles.tab, activeTab === 'unread' && styles.activeTab]}
                    >
                        <Text style={[styles.tabText, activeTab === 'unread' && styles.activeTabText]}>
                            Unread ({notifications.filter(n => !n.read).length})
                        </Text>
                    </Pressable>
                    <Pressable 
                        onPress={() => setActiveTab('critical')} 
                        style={[styles.tab, activeTab === 'critical' && styles.activeTab]}
                    >
                        <Text style={[styles.tabText, activeTab === 'critical' && styles.activeTabText]}>
                            Critical ({notifications.filter(n => n.type === 'critical').length})
                        </Text>
                    </Pressable>
                </View>

                <Divider />

                {/* Notifications Scroll Area */}
                <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollContent}>
                    {filteredNotifications.length === 0 ? (
                        <View style={styles.emptyState}>
                            <IconButton icon="bell-off-outline" size={48} iconColor="#A0A0A0" />
                            <Text variant="titleSmall" style={styles.emptyText}>
                                {activeTab === 'unread' 
                                    ? 'No unread notifications' 
                                    : activeTab === 'critical' 
                                    ? 'No critical issues pending' 
                                    : 'No notifications yet'}
                            </Text>
                            <Text style={styles.emptySubtext}>
                                We'll let you know when environmental alerts or compliance tasks arise.
                            </Text>
                            <Button 
                                mode="outlined" 
                                style={styles.demoBtn} 
                                onPress={seedDemoNotification}
                                textColor="#4BB75F"
                                borderColor="#4BB75F"
                            >
                                Generate Test Alert
                            </Button>
                        </View>
                    ) : (
                        filteredNotifications.map((item) => {
                            const iconInfo = getIconInfo(item.type);
                            return (
                                <Pressable 
                                    key={item.id} 
                                    onPress={() => handleNotificationClick(item)}
                                    style={({ pressed }) => [
                                        styles.itemContainer,
                                        !item.read && styles.unreadItem,
                                        pressed && styles.pressedItem
                                    ]}
                                >
                                    {/* Left Side Icon */}
                                    <View style={[styles.iconWrapper, { backgroundColor: iconInfo.bg }]}>
                                        <IconButton 
                                            icon={iconInfo.icon} 
                                            iconColor={iconInfo.color} 
                                            size={20} 
                                            style={styles.notifIcon}
                                        />
                                    </View>

                                    {/* Center Info */}
                                    <View style={styles.contentWrapper}>
                                        <View style={styles.itemHeader}>
                                            <Text variant="labelLarge" style={[styles.itemTitle, !item.read && styles.unreadText]}>
                                                {item.title}
                                            </Text>
                                            <Text style={styles.itemTime}>
                                                {item.timestamp}
                                            </Text>
                                        </View>
                                        <Text variant="bodyMedium" style={styles.itemDescription} numberOfLines={2}>
                                            {item.description}
                                        </Text>
                                        {item.category && (
                                            <View style={styles.tagBadge}>
                                                <Text style={styles.tagText}>{item.category}</Text>
                                            </View>
                                        )}
                                    </View>

                                    {/* Right Side Actions */}
                                    <View style={styles.rightActionWrapper}>
                                        {!item.read && <View style={styles.unreadDot} />}
                                        <IconButton 
                                            icon="delete-outline" 
                                            size={16} 
                                            iconColor="#808080"
                                            style={styles.deleteIcon}
                                            onPress={() => clearNotification(item.id)}
                                        />
                                    </View>
                                </Pressable>
                            );
                        })
                    )}
                </ScrollView>

                <Divider />

                {/* Footer */}
                {notifications.length > 0 && (
                    <View style={styles.footer}>
                        <Button 
                            mode="text" 
                            onPress={clearAll} 
                            textColor="#BA1A1A"
                            labelStyle={styles.footerActionText}
                        >
                            Clear All
                        </Button>
                        <Button 
                            mode="contained" 
                            onPress={onClose} 
                            buttonColor="#102B27"
                            textColor="#FFF"
                            labelStyle={styles.footerCloseText}
                        >
                            Close
                        </Button>
                    </View>
                )}
            </Card>
        </Portal>
    );
};

const styles = StyleSheet.create({
    cardContainer: {
        position: 'absolute',
        top: 60,
        right: 16,
        width: 360,
        maxHeight: 500,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(16, 43, 39, 0.08)',
        shadowColor: '#102B27',
        shadowOffset: {
            width: 0,
            height: 8,
        },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 10,
        zIndex: 9999,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: '#F8FAF9',
    },
    headerTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    headerTitle: {
        fontWeight: '700',
        color: '#102B27',
    },
    headerBadge: {
        backgroundColor: '#BA1A1A',
        color: '#FFFFFF',
        fontWeight: '700',
    },
    headerActionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
    },
    actionBtnText: {
        fontSize: 12,
        fontWeight: '600',
    },
    closeIcon: {
        margin: 0,
    },
    tabContainer: {
        flexDirection: 'row',
        paddingHorizontal: 12,
        paddingVertical: 6,
        backgroundColor: '#F8FAF9',
        gap: 4,
    },
    tab: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
        backgroundColor: 'transparent',
    },
    activeTab: {
        backgroundColor: '#E6F4ED',
    },
    tabText: {
        fontSize: 12,
        fontWeight: '500',
        color: '#606060',
    },
    activeTabText: {
        color: '#102B27',
        fontWeight: '700',
    },
    scrollContainer: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    scrollContent: {
        paddingVertical: 4,
        flexGrow: 1,
    },
    emptyState: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        textAlign: 'center',
    },
    emptyText: {
        fontWeight: '600',
        color: '#404040',
        marginTop: 8,
    },
    emptySubtext: {
        fontSize: 12,
        color: '#808080',
        textAlign: 'center',
        marginTop: 4,
        lineHeight: 16,
        paddingHorizontal: 12,
    },
    demoBtn: {
        marginTop: 16,
        borderRadius: 8,
    },
    itemContainer: {
        flexDirection: 'row',
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#F5F5F5',
        backgroundColor: '#FFFFFF',
        alignItems: 'flex-start',
    },
    unreadItem: {
        backgroundColor: '#F4FAF7',
    },
    pressedItem: {
        backgroundColor: '#EDF5F1',
    },
    iconWrapper: {
        width: 36,
        height: 36,
        borderRadius: 18,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 2,
    },
    notifIcon: {
        margin: 0,
    },
    contentWrapper: {
        flex: 1,
        marginLeft: 12,
        marginRight: 8,
    },
    itemHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 8,
    },
    itemTitle: {
        fontWeight: '500',
        color: '#404040',
        flex: 1,
    },
    unreadText: {
        fontWeight: '700',
        color: '#102B27',
    },
    itemTime: {
        fontSize: 10,
        color: '#808080',
    },
    itemDescription: {
        color: '#606060',
        marginTop: 2,
        fontSize: 12,
        lineHeight: 16,
    },
    tagBadge: {
        alignSelf: 'flex-start',
        backgroundColor: '#EBEBEB',
        borderRadius: 4,
        paddingHorizontal: 6,
        paddingVertical: 2,
        marginTop: 6,
    },
    tagText: {
        fontSize: 9,
        fontWeight: '600',
        color: '#505050',
    },
    rightActionWrapper: {
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: '100%',
        minWidth: 20,
    },
    unreadDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#4BB75F',
    },
    deleteIcon: {
        margin: 0,
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        backgroundColor: '#F8FAF9',
    },
    footerActionText: {
        fontSize: 13,
        fontWeight: '600',
    },
    footerCloseText: {
        fontSize: 13,
        fontWeight: '600',
    },
});

export default NotificationPanel;
